import React from 'react';
import { useTranslation } from 'react-i18next';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import styled from 'styled-components';
import StyledCard from '../../../shared/layout/cards/StyledCard';

const POSIntegration = () => {
    const { t } = useTranslation();

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:6001/api';
    const integrationBaseUrl = `${API_URL}/v1/integration`;

    const swaggerSpec = {
        openapi: "3.0.0",
        info: {
            title: "DigiWarranty Integration API",
            version: "1.0.0",
            description: "Complete API documentation for integrating external systems (POS, E-commerce, ERP) with DigiWarranty platform. Use these endpoints to sync products, customers, transactions, and manage warranties."
        },
        servers: [
            {
                url: integrationBaseUrl,
                description: "Integration API Server"
            }
        ],
        tags: [
            { name: "Authentication", description: "API authentication and credential management" },
            { name: "Products", description: "Product catalog synchronization" },
            { name: "Categories", description: "Category mapping between systems" },
            { name: "Customers", description: "Customer management and lookup" },
            { name: "Transactions", description: "Sales transaction processing" },
            { name: "Inventory", description: "Inventory synchronization" },
            { name: "Webhooks", description: "Webhook management for real-time updates" },
            { name: "Health", description: "API health and monitoring" }
        ],
        components: {
            securitySchemes: {
                ApiKeyAuth: {
                    type: "apiKey",
                    in: "header",
                    name: "X-API-Key",
                    description: "API key provided after registration"
                }
            },
            schemas: {
                Product: {
                    type: "object",
                    required: ["external_id", "name"],
                    properties: {
                        external_id: { type: "string", description: "Unique product ID in your system", example: "SKU-12345" },
                        name: { type: "string", description: "Product name", example: "Samsung Galaxy S24" },
                        sku: { type: "string", description: "Stock keeping unit", example: "SM-S921B" },
                        ean: { type: "string", description: "EAN/Barcode", example: "8806095300016" },
                        price: { type: "number", format: "float", description: "Selling price", example: 899.99 },
                        cost_price: { type: "number", format: "float", description: "Cost/purchase price", example: 750.00 },
                        category_id: { type: "string", description: "External category ID", example: "electronics-phones" },
                        manufacturer: { type: "string", description: "Manufacturer/brand name", example: "Samsung" },
                        model_number: { type: "string", description: "Model number", example: "SM-S921B/DS" },
                        description: { type: "string", description: "Product description" },
                        warranty_months: { type: "integer", description: "Default warranty period in months", example: 24 },
                        attributes: { type: "object", description: "Additional product attributes", additionalProperties: true }
                    }
                },
                Customer: {
                    type: "object",
                    required: ["external_id"],
                    properties: {
                        external_id: { type: "string", description: "Unique customer ID in your system", example: "CUST-001" },
                        email: { type: "string", format: "email", description: "Customer email", example: "john@example.com" },
                        phone: { type: "string", description: "Phone number with country code", example: "+385912345678" },
                        first_name: { type: "string", description: "First name", example: "John" },
                        last_name: { type: "string", description: "Last name", example: "Doe" },
                        address: { type: "string", description: "Street address", example: "123 Main St" },
                        city: { type: "string", description: "City", example: "Zagreb" },
                        postal_code: { type: "string", description: "Postal/ZIP code", example: "10000" },
                        country_code: { type: "string", description: "ISO country code", example: "HR" }
                    }
                },
                TransactionItem: {
                    type: "object",
                    required: ["external_product_id", "quantity", "price"],
                    properties: {
                        external_product_id: { type: "string", description: "Product ID from your system", example: "SKU-12345" },
                        quantity: { type: "integer", description: "Quantity sold", example: 1 },
                        price: { type: "number", format: "float", description: "Unit price at sale", example: 899.99 },
                        cost_price: { type: "number", format: "float", description: "Unit cost price", example: 750.00 },
                        discount: { type: "number", format: "float", description: "Discount amount", example: 50.00 },
                        serial_number: { type: "string", description: "Item serial number", example: "SN123456789" }
                    }
                },
                Transaction: {
                    type: "object",
                    required: ["external_id", "items"],
                    properties: {
                        external_id: { type: "string", description: "Unique transaction ID in your system", example: "TRX-2024-001" },
                        external_customer_id: { type: "string", description: "Customer ID from your system", example: "CUST-001" },
                        customer_email: { type: "string", format: "email", description: "Customer email (for lookup)", example: "john@example.com" },
                        customer_phone: { type: "string", description: "Customer phone (for lookup)", example: "+385912345678" },
                        venue_id: { type: "string", format: "uuid", description: "Venue/store UUID" },
                        items: { type: "array", items: { "$ref": "#/components/schemas/TransactionItem" } },
                        total_amount: { type: "number", format: "float", description: "Transaction total", example: 899.99 },
                        tax_amount: { type: "number", format: "float", description: "Tax amount", example: 179.99 },
                        payment_method: { type: "string", enum: ["cash", "card", "transfer", "other"], example: "card" },
                        transaction_date: { type: "string", format: "date-time", description: "Transaction timestamp", example: "2024-01-15T14:30:00Z" },
                        metadata: { type: "object", description: "Additional transaction data", additionalProperties: true }
                    }
                },
                CategoryMapping: {
                    type: "object",
                    required: ["external_category_id", "internal_category_id"],
                    properties: {
                        external_category_id: { type: "string", description: "Category ID in your system", example: "electronics-phones" },
                        external_category_name: { type: "string", description: "Category name for display", example: "Mobile Phones" },
                        internal_category_id: { type: "string", format: "uuid", description: "DigiWarranty category UUID" }
                    }
                },
                Webhook: {
                    type: "object",
                    required: ["url", "events"],
                    properties: {
                        url: { type: "string", format: "uri", description: "Webhook endpoint URL", example: "https://your-system.com/webhooks/digiwarranty" },
                        events: {
                            type: "array",
                            items: {
                                type: "string",
                                enum: ["transaction.created", "warranty.activated", "warranty.expiring", "customer.created", "product.synced"]
                            },
                            description: "Events to subscribe to",
                            example: ["transaction.created", "warranty.activated"]
                        },
                        secret: { type: "string", description: "Webhook signing secret (auto-generated if not provided)" },
                        is_active: { type: "boolean", description: "Enable/disable webhook", default: true }
                    }
                },
                InventoryItem: {
                    type: "object",
                    required: ["external_product_id", "quantity"],
                    properties: {
                        external_product_id: { type: "string", description: "Product ID from your system", example: "SKU-12345" },
                        quantity: { type: "integer", description: "Current stock quantity", example: 50 },
                        venue_id: { type: "string", format: "uuid", description: "Venue/warehouse UUID" },
                        reserved: { type: "integer", description: "Reserved quantity", example: 5 },
                        reorder_point: { type: "integer", description: "Reorder threshold", example: 10 }
                    }
                },
                Error: {
                    type: "object",
                    properties: {
                        error: { type: "string", description: "Error message" },
                        code: { type: "string", description: "Error code" },
                        details: { type: "object", description: "Additional error details" }
                    }
                },
                Success: {
                    type: "object",
                    properties: {
                        success: { type: "boolean", example: true },
                        message: { type: "string" },
                        data: { type: "object" }
                    }
                }
            }
        },
        paths: {
            "/register": {
                post: {
                    tags: ["Authentication"],
                    summary: "Register new integration account",
                    description: "Create a new integration account and receive API credentials. This is a public endpoint.",
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    required: ["organization_name", "email", "integration_type"],
                                    properties: {
                                        organization_name: { type: "string", description: "Your organization name", example: "My Store" },
                                        email: { type: "string", format: "email", description: "Contact email", example: "admin@mystore.com" },
                                        integration_type: { type: "string", enum: ["generic", "pos", "ecommerce", "erp"], example: "pos" },
                                        webhook_url: { type: "string", format: "uri", description: "Optional webhook URL" },
                                        config: { type: "object", description: "Integration-specific configuration" }
                                    }
                                }
                            }
                        }
                    },
                    responses: {
                        201: {
                            description: "Account created successfully",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean", example: true },
                                            message: { type: "string", example: "Integration account created successfully" },
                                            data: {
                                                type: "object",
                                                properties: {
                                                    integrator_id: { type: "string", format: "uuid" },
                                                    api_key: { type: "string", description: "Your API key - save this securely!" },
                                                    organization_id: { type: "string", format: "uuid" }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        },
                        422: { description: "Validation error" }
                    }
                }
            },
            "/auth/test": {
                get: {
                    tags: ["Authentication"],
                    summary: "Test API authentication",
                    description: "Verify your API key is valid and check account status",
                    security: [{ ApiKeyAuth: [] }],
                    responses: {
                        200: {
                            description: "Authentication successful",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean", example: true },
                                            message: { type: "string", example: "Authentication successful" },
                                            integrator: {
                                                type: "object",
                                                properties: {
                                                    id: { type: "string", format: "uuid" },
                                                    name: { type: "string" },
                                                    is_active: { type: "boolean" }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        },
                        401: { description: "Invalid or missing API key" }
                    }
                }
            },
            "/auth/rotate-credentials": {
                post: {
                    tags: ["Authentication"],
                    summary: "Rotate API credentials",
                    description: "Generate a new API key. The old key will be invalidated immediately.",
                    security: [{ ApiKeyAuth: [] }],
                    responses: {
                        200: {
                            description: "New credentials generated",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean", example: true },
                                            api_key: { type: "string", description: "Your new API key" },
                                            message: { type: "string", example: "Credentials rotated successfully" }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/products": {
                post: {
                    tags: ["Products"],
                    summary: "Create or update a product",
                    description: "Sync a single product from your system. If external_id exists, the product will be updated.",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/Product" }
                            }
                        }
                    },
                    responses: {
                        200: {
                            description: "Product synced successfully",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean", example: true },
                                            product_id: { type: "string", format: "uuid", description: "DigiWarranty product ID" },
                                            external_id: { type: "string" },
                                            action: { type: "string", enum: ["created", "updated"] }
                                        }
                                    }
                                }
                            }
                        },
                        422: { description: "Validation error" }
                    }
                },
                get: {
                    tags: ["Products"],
                    summary: "List synced products",
                    description: "Retrieve products that have been synced from your system",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "page", in: "query", schema: { type: "integer", default: 1 } },
                        { name: "per_page", in: "query", schema: { type: "integer", default: 50, maximum: 100 } },
                        { name: "search", in: "query", schema: { type: "string" }, description: "Search by name, SKU, or EAN" },
                        { name: "category_id", in: "query", schema: { type: "string" }, description: "Filter by external category ID" }
                    ],
                    responses: {
                        200: {
                            description: "List of products",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            data: { type: "array", items: { "$ref": "#/components/schemas/Product" } },
                                            meta: {
                                                type: "object",
                                                properties: {
                                                    current_page: { type: "integer" },
                                                    total: { type: "integer" },
                                                    per_page: { type: "integer" }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/products/batch": {
                post: {
                    tags: ["Products"],
                    summary: "Batch sync products",
                    description: "Sync multiple products at once (max 100 per request)",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    required: ["products"],
                                    properties: {
                                        products: {
                                            type: "array",
                                            items: { "$ref": "#/components/schemas/Product" },
                                            maxItems: 100
                                        }
                                    }
                                }
                            }
                        }
                    },
                    responses: {
                        200: {
                            description: "Batch sync results",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            processed: { type: "integer", description: "Number of products processed" },
                                            created: { type: "integer" },
                                            updated: { type: "integer" },
                                            failed: { type: "integer" },
                                            errors: { type: "array", items: { type: "object" } }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/products/{external_id}": {
                get: {
                    tags: ["Products"],
                    summary: "Get product by external ID",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "external_id", in: "path", required: true, schema: { type: "string" } }
                    ],
                    responses: {
                        200: {
                            description: "Product details",
                            content: {
                                "application/json": {
                                    schema: { "$ref": "#/components/schemas/Product" }
                                }
                            }
                        },
                        404: { description: "Product not found" }
                    }
                }
            },
            "/categories": {
                get: {
                    tags: ["Categories"],
                    summary: "List available categories",
                    description: "Get list of DigiWarranty categories available for mapping",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "search", in: "query", schema: { type: "string" } }
                    ],
                    responses: {
                        200: {
                            description: "List of categories",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "array",
                                        items: {
                                            type: "object",
                                            properties: {
                                                id: { type: "string", format: "uuid" },
                                                name: { type: "string" },
                                                parent_id: { type: "string", format: "uuid" }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/categories/mappings": {
                post: {
                    tags: ["Categories"],
                    summary: "Create category mapping",
                    description: "Map your external category to a DigiWarranty category",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/CategoryMapping" }
                            }
                        }
                    },
                    responses: {
                        200: { description: "Mapping created successfully" },
                        422: { description: "Validation error" }
                    }
                },
                get: {
                    tags: ["Categories"],
                    summary: "List category mappings",
                    description: "Get all your category mappings",
                    security: [{ ApiKeyAuth: [] }],
                    responses: {
                        200: {
                            description: "List of mappings",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "array",
                                        items: { "$ref": "#/components/schemas/CategoryMapping" }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/customers": {
                post: {
                    tags: ["Customers"],
                    summary: "Create or update customer",
                    description: "Sync customer data. If external_id exists, customer will be updated.",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/Customer" }
                            }
                        }
                    },
                    responses: {
                        200: {
                            description: "Customer synced successfully",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            customer_id: { type: "string", format: "uuid" },
                                            action: { type: "string", enum: ["created", "updated"] }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/customers/lookup": {
                get: {
                    tags: ["Customers"],
                    summary: "Lookup customer",
                    description: "Find existing customer by email, phone, or external ID",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "email", in: "query", schema: { type: "string" } },
                        { name: "phone", in: "query", schema: { type: "string" } },
                        { name: "external_id", in: "query", schema: { type: "string" } }
                    ],
                    responses: {
                        200: {
                            description: "Customer found",
                            content: {
                                "application/json": {
                                    schema: { "$ref": "#/components/schemas/Customer" }
                                }
                            }
                        },
                        404: { description: "Customer not found" }
                    }
                }
            },
            "/customers/{external_id}": {
                get: {
                    tags: ["Customers"],
                    summary: "Get customer by external ID",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "external_id", in: "path", required: true, schema: { type: "string" } }
                    ],
                    responses: {
                        200: {
                            description: "Customer details",
                            content: {
                                "application/json": {
                                    schema: { "$ref": "#/components/schemas/Customer" }
                                }
                            }
                        },
                        404: { description: "Customer not found" }
                    }
                }
            },
            "/transactions": {
                post: {
                    tags: ["Transactions"],
                    summary: "Create transaction",
                    description: "Record a sales transaction. This will create transaction records and activate warranties for eligible items.",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/Transaction" }
                            }
                        }
                    },
                    responses: {
                        201: {
                            description: "Transaction created",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            transaction_id: { type: "string", format: "uuid" },
                                            bill_id: { type: "string", format: "uuid" },
                                            warranties_activated: { type: "integer" },
                                            items_processed: { type: "integer" }
                                        }
                                    }
                                }
                            }
                        },
                        422: { description: "Validation error" }
                    }
                }
            },
            "/transactions/batch": {
                post: {
                    tags: ["Transactions"],
                    summary: "Batch create transactions",
                    description: "Process multiple transactions at once (max 50 per request)",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    required: ["transactions"],
                                    properties: {
                                        transactions: {
                                            type: "array",
                                            items: { "$ref": "#/components/schemas/Transaction" },
                                            maxItems: 50
                                        }
                                    }
                                }
                            }
                        }
                    },
                    responses: {
                        200: {
                            description: "Batch processing results",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            processed: { type: "integer" },
                                            created: { type: "integer" },
                                            failed: { type: "integer" },
                                            errors: { type: "array", items: { type: "object" } }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/transactions/{external_id}": {
                get: {
                    tags: ["Transactions"],
                    summary: "Get transaction by external ID",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "external_id", in: "path", required: true, schema: { type: "string" } }
                    ],
                    responses: {
                        200: {
                            description: "Transaction details",
                            content: {
                                "application/json": {
                                    schema: { "$ref": "#/components/schemas/Transaction" }
                                }
                            }
                        },
                        404: { description: "Transaction not found" }
                    }
                }
            },
            "/inventory/sync": {
                post: {
                    tags: ["Inventory"],
                    summary: "Sync inventory levels",
                    description: "Update stock quantities for multiple products",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    required: ["items"],
                                    properties: {
                                        items: {
                                            type: "array",
                                            items: { "$ref": "#/components/schemas/InventoryItem" }
                                        }
                                    }
                                }
                            }
                        }
                    },
                    responses: {
                        200: {
                            description: "Inventory synced",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            updated: { type: "integer" },
                                            failed: { type: "integer" }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/inventory/status": {
                get: {
                    tags: ["Inventory"],
                    summary: "Get inventory status",
                    description: "Retrieve current inventory levels",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "external_product_ids", in: "query", schema: { type: "string" }, description: "Comma-separated product IDs" },
                        { name: "venue_id", in: "query", schema: { type: "string", format: "uuid" } }
                    ],
                    responses: {
                        200: {
                            description: "Inventory status",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "array",
                                        items: { "$ref": "#/components/schemas/InventoryItem" }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/webhooks": {
                post: {
                    tags: ["Webhooks"],
                    summary: "Create webhook subscription",
                    description: "Subscribe to events and receive real-time notifications",
                    security: [{ ApiKeyAuth: [] }],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/Webhook" }
                            }
                        }
                    },
                    responses: {
                        201: {
                            description: "Webhook created",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            webhook_id: { type: "string", format: "uuid" },
                                            secret: { type: "string", description: "Webhook signing secret - save this!" }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                get: {
                    tags: ["Webhooks"],
                    summary: "List webhooks",
                    description: "Get all registered webhooks",
                    security: [{ ApiKeyAuth: [] }],
                    responses: {
                        200: {
                            description: "List of webhooks",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "array",
                                        items: { "$ref": "#/components/schemas/Webhook" }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/webhooks/{id}": {
                put: {
                    tags: ["Webhooks"],
                    summary: "Update webhook",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "id", in: "path", required: true, schema: { type: "string", format: "uuid" } }
                    ],
                    requestBody: {
                        required: true,
                        content: {
                            "application/json": {
                                schema: { "$ref": "#/components/schemas/Webhook" }
                            }
                        }
                    },
                    responses: {
                        200: { description: "Webhook updated" }
                    }
                },
                delete: {
                    tags: ["Webhooks"],
                    summary: "Delete webhook",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "id", in: "path", required: true, schema: { type: "string", format: "uuid" } }
                    ],
                    responses: {
                        200: { description: "Webhook deleted" }
                    }
                }
            },
            "/webhooks/{id}/test": {
                post: {
                    tags: ["Webhooks"],
                    summary: "Test webhook",
                    description: "Send a test event to your webhook endpoint",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "id", in: "path", required: true, schema: { type: "string", format: "uuid" } }
                    ],
                    responses: {
                        200: {
                            description: "Test result",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            success: { type: "boolean" },
                                            status_code: { type: "integer" },
                                            response_time_ms: { type: "integer" }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/health": {
                get: {
                    tags: ["Health"],
                    summary: "API health check",
                    description: "Check API status and your integration health metrics",
                    security: [{ ApiKeyAuth: [] }],
                    responses: {
                        200: {
                            description: "Health status",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            status: { type: "string", enum: ["healthy", "degraded", "unhealthy"] },
                                            integrator: {
                                                type: "object",
                                                properties: {
                                                    id: { type: "string", format: "uuid" },
                                                    is_active: { type: "boolean" },
                                                    last_sync: { type: "string", format: "date-time" }
                                                }
                                            },
                                            rate_limit: {
                                                type: "object",
                                                properties: {
                                                    limit: { type: "integer" },
                                                    remaining: { type: "integer" },
                                                    reset: { type: "string", format: "date-time" }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            "/logs": {
                get: {
                    tags: ["Health"],
                    summary: "Get API logs",
                    description: "Retrieve recent API call logs for debugging",
                    security: [{ ApiKeyAuth: [] }],
                    parameters: [
                        { name: "page", in: "query", schema: { type: "integer", default: 1 } },
                        { name: "per_page", in: "query", schema: { type: "integer", default: 50 } },
                        { name: "status", in: "query", schema: { type: "string" }, description: "Filter by status code (e.g., 200, 4xx, 5xx)" }
                    ],
                    responses: {
                        200: {
                            description: "API logs",
                            content: {
                                "application/json": {
                                    schema: {
                                        type: "object",
                                        properties: {
                                            data: {
                                                type: "array",
                                                items: {
                                                    type: "object",
                                                    properties: {
                                                        id: { type: "string" },
                                                        endpoint: { type: "string" },
                                                        method: { type: "string" },
                                                        status_code: { type: "integer" },
                                                        response_time_ms: { type: "integer" },
                                                        created_at: { type: "string", format: "date-time" }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    return (
        <Container>
            <StyledCard title={t('POSIntegration.pageTitle')}>
                <SwaggerWrapper>
                    <SwaggerUI spec={swaggerSpec} />
                </SwaggerWrapper>
            </StyledCard>
        </Container>
    );
};

export default POSIntegration;

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const SwaggerWrapper = styled.div`
    .swagger-ui .topbar {
        display: none;
    }

    .swagger-ui .info {
        margin: 1.25rem 0;
    }

    .swagger-ui .info .title {
        font-size: 1.5rem;
        color: #1f2937;
    }

    .swagger-ui .info .description p {
        font-size: 0.875rem;
        color: #6b7280;
    }

    .swagger-ui .scheme-container {
        background: transparent;
        box-shadow: none;
        padding: 0;
    }

    .swagger-ui .opblock-tag {
        font-size: 1rem;
        font-weight: 600;
        color: #374151;
        border-bottom: 1px solid #e5e7eb;
        padding: 0.75rem 0;
    }

    .swagger-ui .opblock {
        border-radius: 8px;
        box-shadow: none;
        border: 1px solid #e5e7eb;
        margin-bottom: 0.5rem;

        .opblock-summary {
            border-bottom: 1px solid #e5e7eb;
            padding: 0.75rem;
        }

        .opblock-summary-method {
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            min-width: 60px;
        }

        .opblock-summary-path {
            font-size: 0.875rem;
            font-weight: 500;
        }
    }

    .swagger-ui .opblock.opblock-get {
        border-color: #93c5fd;
        background: rgba(59, 130, 246, 0.05);

        .opblock-summary-method {
            background: #3b82f6;
        }
    }

    .swagger-ui .opblock.opblock-post {
        border-color: #86efac;
        background: rgba(34, 197, 94, 0.05);

        .opblock-summary-method {
            background: #22c55e;
        }
    }

    .swagger-ui .opblock.opblock-put {
        border-color: #fcd34d;
        background: rgba(245, 158, 11, 0.05);

        .opblock-summary-method {
            background: #f59e0b;
        }
    }

    .swagger-ui .opblock.opblock-delete {
        border-color: #fca5a5;
        background: rgba(239, 68, 68, 0.05);

        .opblock-summary-method {
            background: #ef4444;
        }
    }

    .swagger-ui .btn {
        border-radius: 6px;
        font-weight: 500;
    }

    .swagger-ui .btn.execute {
        background: #821692;
        border-color: #821692;
    }

    .swagger-ui .btn.execute:hover {
        background: #6b1278;
    }

    .swagger-ui .model-box {
        background: #f9fafb;
        border-radius: 8px;
    }

    .swagger-ui table tbody tr td {
        padding: 0.5rem 0.75rem;
    }

    .swagger-ui .parameter__name {
        font-weight: 600;
        color: #374151;
    }

    .swagger-ui .parameter__type {
        color: #6b7280;
        font-size: 0.75rem;
    }

    .swagger-ui .response-col_status {
        font-weight: 600;
    }

    .swagger-ui section.models {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
    }

    .swagger-ui section.models h4 {
        font-size: 1rem;
        padding: 0.75rem 1rem;
        margin: 0;
        background: #f9fafb;
        border-radius: 8px 8px 0 0;
    }

    .swagger-ui .servers-title {
        font-size: 0.875rem;
        font-weight: 600;
    }

    .swagger-ui .servers select {
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        padding: 0.5rem 0.75rem;
    }
`;
